// mvp-returns Phase H17 — Failed Operations action modals
// RetryNowModal, ResolveFailedOpModal, GiveUpFailedOpModal

import { Button, Group, Modal, Stack, Text, Textarea } from "@mantine/core";
import { message } from "../../utils/message";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FailedOpsApi } from "../../apis";

interface BaseProps {
  opened: boolean;
  onClose: () => void;
  opId: string;
  onSuccess: () => void;
}

// ── Retry Now ──────────────────────────────────────────────────────────────

export function RetryNowModal({ opened, onClose, opId, onSuccess }: BaseProps) {
  const { t } = useTranslation("returns");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await FailedOpsApi.retryNow(opId);
      message.success(t("failedOps.retryModal.done"));
      onSuccess();
      onClose();
    } catch (err: unknown) {
      // 409 error.return.failed_op_not_retryable and the other BE keys are translated by message.error.
      message.error(err instanceof Error ? err.message : t("failedOps.genericError"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={t("failedOps.retry")} centered size={440}>
      <Stack gap="md">
        <Text size="sm">
          {t("failedOps.retryModal.body")}
        </Text>
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={busy}>
            {t("templateForm.close")}
          </Button>
          <Button onClick={handleConfirm} loading={busy}>
            {t("failedOps.retry")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Resolve ────────────────────────────────────────────────────────────────

export function ResolveFailedOpModal({ opened, onClose, opId, onSuccess }: BaseProps) {
  const { t } = useTranslation("returns");
  const [notes, setNotes] = useState("");
  const [busy, setBusy]   = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await FailedOpsApi.resolve(opId, notes || undefined);
      message.success(t("failedOps.resolveModal.done"));
      onSuccess();
      onClose();
      setNotes("");
    } catch (err: unknown) {
      // 409 error.return.failed_op_not_retryable and the other BE keys are translated by message.error.
      message.error(err instanceof Error ? err.message : t("failedOps.genericError"));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setNotes("");
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t("failedOps.resolveModal.title")} centered size={440}>
      <Stack gap="md">
        <Textarea
          label={t("failedOps.notesOptional")}
          placeholder={t("failedOps.resolveModal.placeholder")}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={3}
          autosize
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={busy}>
            {t("templateForm.close")}
          </Button>
          <Button onClick={handleConfirm} loading={busy}>
            {t("failedOps.status.RESOLVED")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Give Up ────────────────────────────────────────────────────────────────

export function GiveUpFailedOpModal({ opened, onClose, opId, onSuccess }: BaseProps) {
  const { t } = useTranslation("returns");
  const [notes, setNotes] = useState("");
  const [busy, setBusy]   = useState(false);

  const handleConfirm = async () => {
    setBusy(true);
    try {
      await FailedOpsApi.giveUp(opId, notes || undefined);
      message.success(t("failedOps.giveUpModal.done"));
      onSuccess();
      onClose();
      setNotes("");
    } catch (err: unknown) {
      // 409 error.return.failed_op_not_retryable and the other BE keys are translated by message.error.
      message.error(err instanceof Error ? err.message : t("failedOps.genericError"));
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setNotes("");
    onClose();
  };

  return (
    <Modal opened={opened} onClose={handleClose} title={t("resolutionPanel.cancel")} centered size={440}>
      <Stack gap="md">
        <Text size="sm">
          {t("failedOps.giveUpModal.body")}
        </Text>
        <Textarea
          label={t("failedOps.reasonOptional")}
          placeholder={t("failedOps.giveUpModal.placeholder")}
          value={notes}
          onChange={(e) => setNotes(e.currentTarget.value)}
          minRows={3}
          autosize
        />
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={handleClose} disabled={busy}>
            {t("templateForm.close")}
          </Button>
          <Button onClick={handleConfirm} loading={busy}>
            {t("resolutionPanel.cancel")}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
